export default class BaseEndpoint
{
    constructor(
        appKey,
        {path, responseKey, responseKeyPlural, modelClass, collectionClass}
    ) {
        this.appKey = appKey;
    
        this.path = path;
        this.responseKey = responseKey;
        this.responseKeyPlural = responseKeyPlural;
        this.modelClass = this.defaultModelClass = modelClass;
        this.collectionClass = this.defaultCollectionClass = collectionClass;
        this.eventsEnabled = true;
        this.modelEventsEnabled = true;
    }

    setModelClass(modelClass)
    {
        this.modelClass = modelClass;
        return this;
    }

    resetModelClass()
    {
        this.modelClass = this.defaultModelClass;
        return this;
    }

    setCollectionClass(collectionClass)
    {
        this.collectionClass = collectionClass;
        return this;
    }

    resetCollectionClass()
    {
        this.collectionClass = this.defaultCollectionClass;
        return this;
    }

    getApp()
    {
        return window.TrustUpPro.getApp(this.appKey);
    }

    getAccount()
    {
        return window.TrustUpPro.getAccount(this.appKey);
    }
    
    getUrl()
    {
        return this.getApp() ? this.getApp().direct_url : null;
    }

    getPath()
    {
        return this.path;
    }

    newModel(model)
    {
        return new this.modelClass(model);
    }

    newCollection(models)
    {
        return new this.collectionClass(models);
    }

    async request(path, method = 'GET', data = {})
    {
        let parameters = {
            method: method,
            credentials: this.withCredentials() ? 'include' : 'omit',
            headers: this.getRequestHeaders(),
        };

        if ( method !== 'GET' && data instanceof FormData === false && Object.keys(data).length > 0 ) {
            parameters.body = JSON.stringify(data);
        }

        if ( data instanceof FormData ) {
            parameters = this.getParametersForFormData(parameters, data);
        }

        console.log(
            'Fetch',
            {
                url: `${this.getUrl()}/api/${path}`,
                parameters,
                path,
                method,
                data
            }
        );
        
        return fetch(`${this.getUrl()}/api/${path}`, parameters).then(response => response.json());
    }

    getRequestHeaders()
    {
        let headers = {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Content-Language': window.App?.$store?.state.locale,
            'X-Requested-With': 'XMLHttpRequest'
        };
        
        if ( this.withCredentials() ) {
            headers = {...headers, ...{
                'Authorization': window.App?.$store?.state.token.token_type + ' ' + window.App?.$store?.state.token.access_token,
                'X-TrustUp-Authorization': window.App?.$store?.state.token.token_type + ' ' + window.App?.$store.state.token.access_token,
                'X-TrustUp-JWT': window.App?.$spa?.user?.jwt,
                'X-Account-UUID': this.getAccount()?.uuid
            }}
        }
        
        return headers;
    }

    withCredentials()
    {
        return true;
    }

    getParametersForFormData(parameters, data)
    {
        for ( let pair of data.entries() ) {
            if ( pair[1] == 'null' ) {
                data.set(pair[0], '');
            }
        }

        if ( parameters.method == 'PUT' ) {
            parameters.method = 'POST';
            data.append('_method', 'PUT');
        }

        parameters.body = data;
        delete parameters.headers['Content-Type']; // Let the browser set it with the correct boundary

        return parameters;
    }

    async retrieve(uuid, params = {})
    {
        const searchParams = new URLSearchParams(params);

        let response = await this.request(
            `${this.getPath()}/${uuid}?${searchParams.toString()}`
        );

        let model = response[this.responseKey] || null;
        if ( model ) {
            model = this.newModel(model);
        }

        return model;
    }

    async index(params = {}, path = this.getPath())
    {
        const searchParams = new URLSearchParams(params);

        let data = await this.request(
            `${path}?${searchParams.toString()}`
        );

        data[this.responseKeyPlural] = this.newCollection( data[this.responseKeyPlural] ); 
        
        return data;
    }

    async store(data)
    {
        let response = await this.request(
            this.getPath(),
            'POST',
            data
        );

        let model = response[this.responseKey] || null;
        if ( model ) {
            model = this.newModel(model);
            console.log(`${this.modelClass.getPrefix()}:created`, model);
            this.sendEvent(`${this.modelClass.getPrefix()}:created`, model);
        }

        return model;
    }

    async update({uuid, data, url = null})
    {
        let response = await this.request(
            url || `${this.getPath()}/${uuid}`,
            'PUT',
            data
        );

        let model = response[this.responseKey] || null;
        if ( model ) {
            this.disableModelEvents();
            model = this.newModel(model);
            this.enableModelEvents();
            this.sendEvent(`${this.modelClass.getPrefix()}:updated`, model);
        }

        return model;
    }

    async delete(uuid)
    {
        await this.request(
            `${this.getPath()}/${uuid}`,
            'DELETE'
        );

        this.sendEvent(`${this.modelClass.getPrefix()}:deleted`, uuid);
    }

    async duplicate(uuid)
    {
        let response = await this.request(
            `${this.getPath()}/${uuid}/duplicate`,
            'POST'
        );

        let model = response[this.responseKey] || null;
        if ( model ) {
            model = this.newModel(model);
            this.sendEvent(`${this.modelClass.getPrefix()}:created`, model);
        }

        return model;
    }

    disableModelEvents()
    {
        this.modelEventsEnabled = false;
        return this;
    }

    enableModelEvents()
    {
        this.modelEventsEnabled = true;
        return this;
    }

    disableEvents()
    {
        this.eventsEnabled = false;
        return this;
    }

    enableEvents()
    {
        this.eventsEnabled = true;
        return this;
    }

    sendEvent(event, data)
    {
        if ( ! this.eventsEnabled ) {
            console.error('Events are disabled, this one was not sent', {event, data});
            return;
        }

        console.log('Sending event', {event, data});
        window.App.$bus.$emit(event, data);
    }

}